/* @flow weak */

'use strict';

const Reflux = require('reflux');
const request = require('superagent');
const store = require('store');
const _ = require('lodash');
const Omniture = require('../metrics/walmart/Omniture.js');

const AnswerActions = require('../actions/AnswerActions.jsx');
const VisualizationActions = require('../actions/VisualizationActions.jsx');
const ValidatorActions = require('../actions/ValidatorActions.jsx');

const ProductStore = require('./ProductStore.jsx');
const QuestionStore = require('./QuestionStore.jsx');
const PartStore = require('./PartStore.jsx');
const ValidatorStore = require('../stores/ValidatorStore.jsx');
const PaymentPlanStore = require('./PaymentPlanStore.jsx');
const SKUStore = require('./SKUStore.jsx');

const Config = require('../configs/Config.js');
const Loader = require('../loaders');
const ln = Loader.ln || 'en_US';

import buildAnswerConflictMarkup from '../helpers/BuildAnswerConflictMarkup';

const AnswerStore = Reflux.createStore({
  listenables: [AnswerActions],

  itemCode: undefined,
  sku: undefined,
  possibleSkus: [],
  answers: {},
  restorableAnswers: {},
  images: [],
  submitted: false,
  fusionCheck: false,

  setFusionCheck(check) {
    this.fusionCheck = !!check;
  },

  getFusionCheck() {
    return this.fusionCheck;
  },

  getInitialState() {
    return this.answers;
  },

  triggerAndValidate() {
    this.trigger(this.answers);
    ValidatorActions.validateConfiguration(this.answers);
  },

  onLoad(data = [], dialogCB) {
    let answers = data;

    // set/lock answers based on provided SKU
    if (Config.features.lockAnswersBySku && Loader.sku) {
      // use skuMap to find which questions/parts need to be set/locked
      const skuMap = ProductStore.getSKUMap();
      const skuAnswers = _.chain(skuMap)
        .filter(row => _.includes(row.skus, Loader.sku))
        .map(row => row.pairs)
        .first()
        .reduce((storedPairs, pair) => (
          [...storedPairs, { question_id: pair.q, part_id: pair.p, locked: true }]
        ), [])
        .value();

      const partitionedAnswers = _.partition(answers, answer => (
        _.some(skuAnswers, skuAnswer => (
          skuAnswer.question_id === answer.question_id && skuAnswer.part_id !== answer.part_id
        ))
      ));

      const conflictingAnswers = partitionedAnswers[0];
      const savedAnswers = partitionedAnswers[1];

      if (conflictingAnswers.length > 0) {
        const conflictMessage = `
          Locked configuration for SKU ${Loader.sku} conflicts with answers in the saved configuration being loaded (ID ${Loader.configId}).
          Locked SKU configuration will override saved configuration.
        `;
        const conflictMarkup = buildAnswerConflictMarkup(conflictingAnswers, skuAnswers);

        dialogCB(conflictMessage, conflictMarkup);
      }

      // combine saved answers with sku answers
      answers = [...savedAnswers, ...skuAnswers];
    }

    // build out answers object
    if (answers && answers.length > 0) {
      _.each(answers, (answer) => {
        let question = QuestionStore.getQuestionById(answer.question_id);
        let part = PartStore.getPartByQuestionAndPartId(answer.question_id, answer.part_id);

        if (!question || !part) {
          return;
        }

        this.answers[question.id] = {
          question,
          part,
          input: answer.value,
          locked: answer.locked
        };
      });

      if (store.enabled) {
        store.set('answers', this.answers);
      }
    }

    this.itemCode = this.getItemCode();
    this.possibleSkus = this.getPossibleSkus();
    this.sku = this.getSKU();

    this.triggerAndValidate();
  },

  onUpdate(question, part, input, rememberLastAnswered = true, textInput = false) {
    this.onClearLastAnswered();

    this.answers[question.id] = {
      'question': question,
      'part': part,
      'input': input,
      'lastAnswered': rememberLastAnswered
    };

    if (store.enabled) {
      store.set('answers', this.answers);
    }

    if (textInput) {
      VisualizationActions.delayRenderSync();
    }

    this.itemCode = this.getItemCode();
    this.possibleSkus = this.getPossibleSkus();
    this.sku = this.getSKU();

    this.triggerAndValidate();
  },

  onClearLastAnswered() {
    _.each(this.answers, answer => {
      if (answer.lastAnswered) delete answer.lastAnswered;
    });
  },

  onBatchUpdate(answerData) {
    _.each(answerData, answer => {
      let questionId = _.get(answer, 'question.id');

      if (questionId) {
        this.answers[questionId] = {
          'question': answer.question,
          'part': answer.part,
          'input': answer.input,
        };
      }
    });

    if (store.enabled) {
      store.set('answers', this.answers);
    }

    this.itemCode = this.getItemCode();
    this.possibleSkus = this.getPossibleSkus();
    this.sku = this.getSKU();

    this.triggerAndValidate();
  },

  onDelete(question, restoreable = false) {
    // If answer doesn't exist, nothing for us to do! Return.
    if (!this.answers[question.id]) {
      return;
    }

    if (restoreable) {
      this.restorableAnswers[question.id] = this.answers[question.id];
    }

    delete this.answers[question.id];

    this.itemCode = this.getItemCode();
    this.possibleSkus = this.getPossibleSkus();
    this.sku = this.getSKU();

    this.triggerAndValidate();
  },

  onRestore(question) {
    if (this.restorableAnswers[question.id]) {
      let answer = this.restorableAnswers[question.id];
      delete this.restorableAnswers[question.id];
      this.onUpdate(answer.question, answer.part, answer.input);
    }
  },

  isValidURL(string) {
    let url;
  
    try {
      url = new URL(string);
    } catch (_) {
      return false;  
    }

    return url.protocol === "http:" || url.protocol === "https:";
  },

  onSubmit(validationErrorCallback, errorCB) {
    // Flip the submitted flag to on. Some constraints (like Required) depend on the submitted flag being set to true
    // before displaying the error message. We don't want to show the users errors before they've had a chance to fill
    // out the input.
    ValidatorActions.registerUserSubmission();

    const { valid, invalidQuestions } = ValidatorStore.getValidationData();

    if (!valid) {
      if (invalidQuestions.length > 0) {
        if (validationErrorCallback && typeof validationErrorCallback === 'function') {
          validationErrorCallback(invalidQuestions);

          if (Config.analytics.enableTracking) {
            const trackingTarget = _.get(window, Config.analytics.eventTrackingTarget);

            if (trackingTarget && typeof trackingTarget === 'function') {
              const event = 'uc_notFinishedPopup';
              const category = _.get(Config, 'analytics.eventCategory');
              const action = 'UC-Not Finished Pop-up';
              const label = _.get(location, 'href');

              trackingTarget(event, category, action, label);
            }
          }
        }
      }

      return;
    }

    let payload = this.generatePayload(errorCB);
    if (!__PROD__) {
      console.log(payload);
    }

    if (Config.analytics.enableTracking) {
      const trackingTarget = _.get(window, Config.analytics.eventTrackingTarget);

      if (trackingTarget && typeof trackingTarget === 'function') {
        const event = 'uc_addToCart';
        const category = _.get(Config, 'analytics.eventCategory');
        const action = 'Add to Cart';
        const label = _.get(location, 'href');

        trackingTarget(event, category, action, label);
      }
    }

    if (_.get(Config, 'omniture.account')) {
      Omniture.addToCartTracking(payload.id, 1, payload.price);
    }

    // XHR request to endpoint
    request
      .post(_.get(Config, 'api.saveConfig'))
      .send(payload)
      .end((err, res) => {
        if (typeof _.get(res, 'body') === 'object' && _.get(res, 'body') !== null) {
          if (_.get(res.body, 'status') === 'success') {
            switch (Config.features.addToCartAction) {
              case 'redirect':
                if (res.body.redirect_url && _.isString(res.body.redirect_url) && this.isValidURL(res.body.redirect_url)) {
                  console.log('Redirecting to: ', res.body.redirect_url);
                  window.location = res.body.redirect_url;
                } else {
                  console.warn('Request successful but no redirect URL found in response body.  Doing nothing.');
                }
                break;
              case 'redirect-parent':
                if (res.body.redirect_url && _.isString(res.body.redirect_url) && this.isValidURL(res.body.redirect_url)) {
                  console.log('Redirecting to: ', res.body.redirect_url);
                  parent.window.location = res.body.redirect_url;
                } else {
                  console.warn('Request successful but no redirect URL found in response body.  Doing nothing.');
                }
                break;
              case 'postmessage-parent':
                if (res.body.payload) {
                  window.parent.postMessage(res.body.payload, Config.postmessage.domain);
                }
                break;
              case 'tab':
                if (__VENDOR__ === 'netsuite' && window.uc_debug) {
                  console.log('NetSuite add-to-cart success!  window.uc_debug is set, bypassing window.close.');
                  break;
                }
                window.close();
                break;
              case 'process':
                if (__VENDOR__ === 'aafes') {
                  const form = document.createElement('form');
                  form.setAttribute('method', 'post');
                  form.setAttribute('action', Loader.addToCartUrl);

                  const hiddenField = document.createElement('input');
                  hiddenField.setAttribute('type', 'hidden');
                  hiddenField.setAttribute('name', 'cblencoded');
                  hiddenField.setAttribute('value', encodeURIComponent(_.get(res.body, 'xml') || ''));

                  form.appendChild(hiddenField);

                  document.body.appendChild(form);
                  form.submit();
                }
                break;
            }
          } else {
            errorCB();
            console.log('Add to cart failure. res.body.status is not success. ', err, res);
            if (_.get(Config, 'omniture.account')) {
              Omniture.errorTracking('Add to cart failure. \'res.status\' is ' + res.status);
            }
          }
        } else {
          errorCB();
          console.log(err, res);
          if (_.get(Config, 'omniture.account')) {
            Omniture.errorTracking('An error has occurred on add to cart. \'res.status\' is ' + res.status);
          }
        }
      });

    return false;
  },

  // onSubmitPartial is for saving the current state of the configurations without validating. It's not for adding to cart.
  onSubmitPartial(onSaveCallback) {
    let payload = this.generatePayload();
    if (__DEV__) {
      console.log(payload);
      if (onSaveCallback && typeof onSaveCallback === 'function') {
        onSaveCallback('test_response', payload['image_url']);
      }

      return;
    }

    // XHR request to endpoint
    request
      .post(_.get(Config, 'api.savePartialConfig'))
      .send(payload)
      .end((err, res) => {
        if (typeof _.get(res, 'body') === 'object') {
          if (_.get(res.body, 'status') === 'success') {
            if (onSaveCallback && typeof onSaveCallback === 'function') {
              onSaveCallback(res.body.config_id, payload['image_url']);
            } else {
              console.log('No callback to call');
            }
          } else {
            // @TODO use a dialog for this
            console.log('Submit-partial failure. res.body.status is not success. ', err, res);
            if (_.get(Config, 'omniture.account')) {
              Omniture.errorTracking('Submit-partial failure. \'res.status\' is ' + res.status);
            }
            alert(res.body.error_msg);
          }
        } else {
          // @TODO handle this error better
          console.log(err, res);
          if (_.get(Config, 'omniture.account')) {
            Omniture.errorTracking('An error has occurred on save. \'res.status\' is ' + res.status);
          }
          alert('An error has occurred on save.');
        }
      });

    return false;
  },

  // @TODO: image handling belongs elsewhere
  onSetProductImage(url, index, view) {
    this.images[index] = {
      view: view,
      url: url
    };
  },

  getImages() {
    return this.images;
  },

  getImageUrlByIndex(index) {
    return this.images[index].url;
  },

  getImageUrlByView(view) {
    return _.filter(this.images, image => {
      return image.view === view;
    })[0].url;
  },

  onPrintConfiguration(validationErrorCallback, printCallback) {
    ValidatorActions.displayValidation();

    const { valid, invalidQuestions } = ValidatorStore.getValidationData();

    if (!valid) {
      if (invalidQuestions.length > 0) {
        validationErrorCallback(invalidQuestions);
      }

      return;
    }

    let payload = this.generatePayload();

    if (printCallback && typeof printCallback === 'function') {
      printCallback(payload);
    }
  },

  fetchAll() {
    return this.answers;
  },

  getPossibleSkus() {
    const prices = SKUStore.getSKUPricing();

    return _
      .chain(ProductStore.getSKUMap())
      .filter((row) => {
        return _.every(row.pairs, (pair) => {
          // If the question isn't answered, all possible parts are possible.
          // If the question is answered, only the part selected is possible.
          let notAnswered = _.get(this.answers, [pair.q]) === undefined;
          let selected = _.get(this.answers, [pair.q, 'part', 'id']) === pair.p;
          return notAnswered || selected;
        });
      })
      .map((row) => {
        return row.skus;
      })
      .flatten()
      .filter((sku) => {
        // A mapped SKU is only valid if it has an associated price.
        return _.get(prices, [sku]);
      })
      .value();
  },

  getSKUs() {
    const skuMap = ProductStore.getSKUMap();

    // Extract the questions from the first row in SKU map. Questions are the same for every SKU.
    // const questionIds = _.map(_.get(skuMap, [0, 'pairs']), 'q');

    // // Ensure all questions are answered.
    // const allQuestionsAnswered = _.every(questionIds, (questionId) => {
    //   return !!_.get(this.answers, questionId);
    // });

    // // And if they're not, we return undefined since we cannot determine the SKUs.
    // if (!allQuestionsAnswered) {
    //   return undefined;
    // }

    // Find matching SKUs
    let matchingSkus;
    _.each(skuMap, row => {
      const match = _.every(row.pairs, pair => {
        return pair.p == _.get(this.answers, [pair.q, 'part', 'id']);
      });

      if (match) {
        matchingSkus = row.skus;
        return false; // exit the loop explicitly by returning false
      }
    });

    return matchingSkus;
  },

  getSKU() {
    return this.possibleSkus.length === 1 ? this.possibleSkus[0] : undefined;
  },

  getSkuPricesForPart(part) {
    const prices = SKUStore.getSKUPricing();
    const upchargeSku = _.get(part, 'metadata.SKU');
    if (upchargeSku && ProductStore.getProductLineId() != Config.productline.KSUNIVCollections) {
      const price = _.get(prices, upchargeSku);
      return price ? [price] : [];
    }

    return _
      .chain(ProductStore.getSKUMap())
      .filter((row) => {
        // Filter out all rows that do not have a pair with the part we're fetching price data for
        return ProductStore.getProductLineId() != Config.productline.KSUNIVCollections ? _.some(row.pairs, (pair) => {
          return part.id === pair.p;
        }) : row.pairs[0].p === part.id;
      })
      .filter((row) => {
        // Evaluate every pair for the given SKU map row, filter the row out if any pairs aren't relevant
        return _.every(row.pairs, (pair) => {
          // Current pair passes if any of the following conditions are true:

          // If the pair contains the part we're requesting a price for
          const hasRequestedPart = pair.p == part.id;

          // If the pair has no answer, part doesn't matter
          const notAnswered = _.get(this.answers, [pair.q]) === undefined;

          // If the pair has an answer, and if the current pair part is the same as the answer part
          const selected = _.get(this.answers, [pair.q, 'part', 'id']) == pair.p;
          return hasRequestedPart || notAnswered || selected;
        });
      })
      .map((row) => {
        return row.skus
      })
      .flatten()
      .map((sku) => {
        return _.get(prices, [sku]);
      })
      .compact()
      .sort((a, b) => a.retail - b.retail)
      .value();
  },

  getItemCode(questionId, partId) {
    let itemCode = ProductStore.getItemCode();

    // Simple ItemCode
    if (typeof itemCode === 'string') {
      return itemCode;
    }

    // ItemCode Map
    if (Array.isArray(itemCode)) {
      // Extract the questions from the first item code. Questions are the same for every item code.
      let questionIds = _.map(itemCode[0]['pairs'], 'q');

      // Ensure all questions are answered.
      let allQuestionsAnswered = _.every(questionIds, (questionId) => {
        return !!_.get(this.answers, questionId);
      });

      // And if they're not, we return undefined since we cannot determine the item code.
      if (!allQuestionsAnswered) {
        return undefined;
      }

      let itemCodeMatch;
      _.each(itemCode, row => {
        let match = _.every(row.pairs, pair => {
          // If questionId and partId arguments have been provided, we are looking for what the itemCode would be for the provided part
          // If the current pair question matches provided questionId we check to see if it matches the provided part
          if (questionId && partId && pair.q === questionId) {
            return pair.p === partId;
          }

          // Otherwise we just match against the current answer part for this question
          return pair.p == _.get(this.answers, [pair.q, 'part', 'id']);
        });

        if (match) {
          itemCodeMatch = row.item_code;
          return false; // exit the loop explicitly by returning false
        }
      });

      return itemCodeMatch;
    }

    // "Complex" ItemCode
    let itemCodeQuestion = _.keys(itemCode)[0];
    let itemCodeQuestionId = _.get(QuestionStore.getQuestionByName(itemCodeQuestion), 'id');
    let itemCodeKey = _.get(this.answers, [itemCodeQuestionId, 'part', 'metadata', 'ItemCodeKey']);
    if (!itemCodeKey) {
      return undefined;
    }

    return itemCode[itemCodeQuestion][itemCodeKey];
  },

  getPrice(field = 'retail') {
    if (Config.skuPricing) {
      const matchingSkus = this.getSKUs();

      if (!matchingSkus) {
        return undefined;
      }

      const skuPricing = SKUStore.getSKUPricing();
      const basePrice = _.get(_.find(skuPricing, (value, key) => _.contains(matchingSkus, key)), [field]);

      const upchargePricing = _.chain(this.answers)
        .filter((answer) => {
          return _.get(answer, 'part.metadata.SKU');
        })
        .map((answer) => {
          return _.get(skuPricing, [answer.part.metadata.SKU, field]) || 0;
        })
        .reduce((sum, price) => {
          return parseFloat(sum) + parseFloat(price);
        })
        .value() || 0;

      let price = parseFloat(basePrice) + parseFloat(upchargePricing);

      let greekRoyalty = parseFloat(ProductStore.getGreekRoyalty());
      let applyGreekRoyalty = this.useGreekPricing();

      if (greekRoyalty && applyGreekRoyalty !== false) {
        price = price + greekRoyalty;
      }

      return price;
    } else {
      const fieldMap = {
        'retail': 'retail_price'
      };

      field = _.get(fieldMap, [field]);
      if (!field) {
        return undefined;
      }

      // Let's use some functional programming to grab the price!
      // 1) We're going to filter out any answers who's part does not have a price attached or is a greek price.
      //    Greek pricing get applied later because only one greek fee applies to the product.
      // 2) Then we're going to map the value of the remaining answers to their price.
      // 3) Finally we're going to reduce the collection to a sum of the prices.
      let price = _.chain(this.answers)
        .filter((answer) => {
          return !!answer.part.pricing && !_.get(answer, 'part.metadata.GreekPricing');
        })
        .map((answer) => {
          if (_.values(answer.part.pricing).length === 1) {
            return parseFloat(_.get(_.values(answer.part.pricing)[0], [field]));
          } else {
            return parseFloat(_.get(answer, ['part', 'pricing', this.itemCode, field]));
          }
        })
        .reduce((sum, price) => {
          return parseFloat(sum) + parseFloat(price);
        })
        .value();

      let greekRoyalty = parseFloat(ProductStore.getGreekRoyalty());
      let applyGreekRoyalty = this.useGreekPricing();

      // if there are no parts with ValidateGreek metadata whose input contains Greek characters, do not add GreekRoyalty to price
      if (greekRoyalty && applyGreekRoyalty !== false) {
        price = price + greekRoyalty;
      }

      return price;
    }
  },

  useGreekPricing() {
    let applyGreekRoyalty = false;
    let greekRegex = /[ΑΒΓΔΕΖΗΘΙΚΛΜΝΞΟΠΡΣΤΥΦΧΨΩ]/;

    // check for answer parts that have ValidateGreek metadata and whose input contains Greek characters
    // also check for parts that have GreekPricing metadata
    _.each(this.answers, function (answer) {
      if (_.get(answer.part, 'metadata.ValidateGreek') && greekRegex.test(answer.input)) {
        applyGreekRoyalty = true;
        return true;
      }

      if (_.get(answer, 'part.metadata.GreekPricing')) {
        applyGreekRoyalty = true;
        return true;
      }
    });

    return applyGreekRoyalty;
  },

  getPromoCode() {
    if (Config.skuPricing) {
      const skuPricing = SKUStore.getSKUPricing();
      const sku = this.getSKU();
      if (!sku) return ''

      const basePromoCode = _.get(skuPricing, [sku, 'promo_code']) || '';

      const upchargePromos = _.chain(this.answers)
        .filter(answer => {
          // Ensure the part has a SKU, the SKU has a price, and the price has a promo code
          const upchargeSKU = _.get(answer.part, 'metadata.SKU');
          const upchargePrice = _.get(skuPricing, upchargeSKU);
          const promoCode = _.get(upchargePrice, 'promo_code');

          return promoCode && promoCode != '0';
        })
        .map(answer => {
          return skuPricing[answer.part.metadata.SKU].promo_code
        })
        .value();

      return [basePromoCode, ...upchargePromos].join()
    }

    return _.chain(this.answers)
      .filter((answer) => {
        return !!answer.part.pricing;
      })
      .map((answer) => {
        if (_.values(answer.part.pricing).length === 1) {
          return _.values(answer.part.pricing)[0].promo_code;
        } else {
          return answer.part.pricing[this.itemCode].promo_code;
        }
      })
      .filter((promoCode) => {
        return !!promoCode;
      })
      .reduce((result, promoCode) => {
        result.push(promoCode);
        return result;
      }, [])
      .value()
      .toString();
  },

  getStartingAtPrice() {
    if (Config.skuPricing) {
      const skuMap = ProductStore.getSKUMap();
      const baseSkus = _.chain(skuMap).map('skus').flatten().value();

      const skuPricing = SKUStore.getSKUPricing();
      const sortedPrices = _.chain(skuPricing)
        .filter(price => _.includes(baseSkus, price.sku))
        .map(skuPrices => skuPrices.retail)
        .value()
        .sort((a, b) => a - b);

      return sortedPrices[0];
    } else {
      let itemCode = this.itemCode || this.getItemCode();

      if (typeof itemCode === 'string') {
        return _.get(PartStore.fetchLowestPriceForItemCode(itemCode), 'retail_price');
      } else if (typeof itemCode === 'undefined') {
        itemCode = ProductStore.getItemCode();
      }

      let itemCodes;

      if (Array.isArray(itemCode)) {
        // ItemCode Map
        itemCodes = _.map(itemCode, ic => ic.item_code);
      } else {
        // "Complex" ItemCode
        itemCodes = _.first(_.values(itemCode));
      }

      let lowestPrices = _.map(itemCodes, (ic) => {
        return _.get(PartStore.fetchLowestPriceForItemCode(ic), 'retail_price');
      }).sort(function (a, b) {
        return parseFloat(a) - parseFloat(b);
      });

      // @TODO temp to fix display of undefined
      if (lowestPrices[0]) {
        return lowestPrices[0];
      } else {
        console.log('Could not find starting at price.');
        return undefined;
      }
    }
  },

  generatePayload(errorCB) {
     let addToCartUrl = Loader.addToCartUrl;
    // if (typeof addToCartUrl === 'undefined') {
    //   errorCB();
    //   throw new Error('No add to cart URL');
    // }

    const priceRetail = this.getPrice('retail');
    const priceRetailFull = this.getPrice('retail_full');
    const priceNet = this.getPrice('net');
    const priceNetFull = this.getPrice('net_full');

    let smi = ProductStore.getSmi();
    let schoolCode = ProductStore.getSchoolCode();
    const answers = _.chain(this.answers)
      .sortBy(answer => {
        return parseInt(answer.question.sort)
      })
      .map(answer => {
        if (answer.question.name == "SchoolSmi" && answer.input != null) {
          smi = answer.input;
        }

        if (answer.question.name == "SchoolCode" && answer.input != null) {
          schoolCode = answer.input;
        }

        var partDisplayName = _.get(answer.part, `metadata.lang.${ln}.DisplayName`);
        
        return {
          question: {
            id: answer.question.id,
            name: _.get(answer.question, `metadata.lang.${ln}.DisplayName`) || answer.question.name,
            db_name: answer.question.name
          },
          part: {
            id: _.get(answer.part, 'id') || null,
            name: (partDisplayName && typeof partDisplayName == 'string' ? partDisplayName : _.get(answer.part, 'name')) || null,
            db_name: _.get(answer.part, 'name') || null,
            item_code: _.get(answer.part, 'metadata.ItemCode') || null,
            metal_code: _.get(answer.part, 'metadata.MetalCode') || null
          },
          'value': answer.input
        }
      })
      .value()

    let payload = {
      'id': ProductStore.getProductId(),
      'offerId': Loader.offerId,
      'school_id': ProductStore.getSchoolId(),
      'school_code': schoolCode,
      'smi': smi,
      'item_code': this.itemCode,
      'sku': this.getSKU() || null,
      'price': priceRetail,
      'promo_code': this.getPromoCode(),
      'payment_plan': PaymentPlanStore.getIsAvailable() && PaymentPlanStore.getIsActive(),
      'url': addToCartUrl,
      'redirectUrl': Loader.redirectUrl,
      'image_url': _.get(this.images[0], 'url'),
      'pricing': {
        'retail': priceRetail,
        'retail_full': priceRetailFull,
        'net': priceNet,
        'net_full': priceNetFull,
        'retail_difference': priceRetailFull - priceRetail,
        'net_difference': priceNetFull - priceNet
      },
      'answers': answers,
      'productUrl': Loader.productUrl
    };

    if (__VENDOR__ === 'netsuite') {
      payload = Object.assign({}, payload, {
        uid: Loader.uid || null,
        custrec: Loader.customer || null
      });
    }

    if (__VENDOR__ === 'zales' || __VENDOR__ === 'signet') {
      payload = Object.assign({}, payload, {
        site_id: Loader.siteId || null,
        user_id: Loader.userId || null,
        cart_id: Loader.cartId || null,
        variant_id: Loader.variantId || null,
        domain: Loader.domain || null,
        env_id: Loader.envId || null,
        url: Loader.url || null
      });
    }

    if (__VENDOR__ === 'jcp') {
      payload = Object.assign({}, payload, {
        shopperID: Loader.shopperID,
        itemID: Loader.itemID,
        magentoSku: Loader.magentoSku
      });
    }

    return payload;
  }
});

module.exports = AnswerStore;



// WEBPACK FOOTER //
// ./src/base/scripts/stores/AnswerStore.jsx